import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { takeUntil } from 'rxjs';
import { FormGroup, FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { faSave, faX } from '@fortawesome/free-solid-svg-icons';
import { SVGIcon, saveIcon, xIcon } from '@progress/kendo-svg-icons';
import { DetailService } from '@core/services/detail.service';
import { ToolbarItem } from '../../interfaces/tool-bar-item.interface';
import { BaseControl } from '../../ui/controls/base-control.model';
import { DateControl } from '../../ui/controls/date-control.model';
import { TabControl } from '../../ui/controls/tab-control.model';
import { TextControl } from '../../ui/controls/text-control.model';
import { TextAreaControl } from '../../ui/controls/textarea-control.model';
import { ToggleControl } from '../../ui/controls/toggle-control.model';
import { UiGroupControl } from '../../ui/controls/ui-group-control.model';
import { ViewControl } from '../../ui/controls/view-control.model';
import { BaseComponent } from '@core/models/base-component.model';
import { SelectControl } from '../../ui/controls/select-control.model';
import { FeedbackMessageService } from '@shared/services/feedback-message.service';
import { EntityService } from '../../services/entity.service';
import { MoneyControl } from '../../ui/controls/money-control.model';
import { HttpErrorResponse } from '@angular/common/http';
import { INotificationService } from '../../services/inotification.service';
import { UrlControl } from '../../ui/controls/url-control.model';
import { EntityTypeAttributeTabControl } from '../../ui/controls/entity-type-attribute-tab-control.model';
import { RadioControl } from '../../ui/controls/radio-control.model';
import { PasswordControl } from '../../ui/controls/password-control.model';
import { EditorControl } from '../../ui/controls/editor-control.model';
import { UploadControl } from '../../ui/controls/upload-control.model';
import { AuthenticationService } from '../../../core/services/authentication.serivce';
import { GridControl } from '../../ui/controls/grid-control.model';
import { NumericControl } from '../../ui/controls/numeric-control.model';
import { ViewRowControl } from '../../ui/controls/view-row-control.model';
import { CardModule, ExpansionPanelModule, StackLayoutModule, TabStripModule } from '@progress/kendo-angular-layout';
import { ToolbarListComponent } from '../tool-bar/tool-bar.component';
import { TabComponent } from '../tab/tab.component';
import { BreadCrumbModule } from '@progress/kendo-angular-navigation';
import { FormControlComponent } from '../form-control/form-control.component';
import { FormRowComponent } from '../form-row/form-row.component';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { KendoCardFormRowComponent } from '../kendo-card-form-row/kendo-card-form-row.component';
import { KendoCardFormControlComponent } from '../kendo-card-form-control/kendo-card-form-control.component';
import { GridModule } from '@progress/kendo-angular-grid';
import { GridKendoCardComponent } from '../grid-kendo-card/grid-kendo-card.component';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { RouterExtensionsService } from '../../../core/services/router-extensions.service';
import { OverlayService } from '../../services/overlay.service';
import { ListBoxControl } from '../../ui/controls/listbox-control.model';

type ViewType = 'tab' | 'compressed';

@Component({
    selector: 'app-detail-layout',
    imports: [
        StackLayoutModule,
        ReactiveFormsModule,
        ToolbarListComponent,
        KendoCardFormRowComponent,
        TabComponent,
        GridModule,
        BreadCrumbModule,
        GridKendoCardComponent,
        KendoCardFormControlComponent,
        FormControlComponent,
        ExpansionPanelModule,
        FormRowComponent,
        TabStripModule,
        TranslateModule,
        ButtonModule,
        LoaderModule,
        CardModule,
        DialogModule
    ],
    providers: [FeedbackMessageService],
    templateUrl: './detail-layout.component.html',
    styleUrls: ['./detail-layout.component.css']
})
export class DetailLayoutComponent extends BaseComponent implements OnInit {

  @Input() id: string;
  @Input() name: string;
  @Input() apiController: string;
  @Input() viewType?: ViewType;
  @Input() views: ViewControl[];
  @Input() showSaveButton: boolean = true
  @Input() showRemoveButton: boolean = true;
  @Input() removeDefaultToolbarItems: boolean = false;
  @Input() keyFieldName: string;
  @Input() breadCrumbPropertyNames?: string[];

  @Input() toolbarItems: ToolbarItem[] = [];

  @Input() entity: any;
  @Output() entityChange: EventEmitter<any> = new EventEmitter<any>();

  @Output() entityLoaded: EventEmitter<any> = new EventEmitter();

  // Optional
  // Specifies if the save() output acts as a pre-save function or replaces the built-in save function
  @Input() preSave: boolean | null = null;
  // Custom save function
  @Output() save: EventEmitter<any> = new EventEmitter();

  detailForm: FormGroup;
  formControls: { [k: string]: FormControl } = {};
  submitted: boolean = false;

  public test: { [key: string]: [] } = {
    files: [],
  };

  openDialog: boolean = false;
  dialogNo: SVGIcon = xIcon;
  dialogYes: SVGIcon = saveIcon;
  dialogLoader: boolean = false;
  dialogIcon: boolean = true;

  constructor(private detailService: DetailService<any>,
              private formBuilder: FormBuilder,
              private feedback: FeedbackMessageService,
              private router: Router,
              private notificationService: INotificationService,
              private overlayService: OverlayService,
              private auth: AuthenticationService,
              private cd: ChangeDetectorRef) { super(); }

  ngOnInit() {
    this.bName = this.name;

    var form = this.formBuilder.group({});
    var allControls: BaseControl[] = this.flatten();
    for (var i = 0; i < allControls.length; i++) {
      var control = allControls[i];
      var isToggle = control instanceof ToggleControl;
      var value: string | undefined = '';
      var isNumeric = control instanceof NumericControl;
      if (isNumeric) {
        value = undefined;
      }
      if (isToggle) {
        value = Reflect.get(control, "value");
      }
      var options = {
        value: value,
        disabled: control.isReadOnly
      };
      var id: string = control.id;
      if (control instanceof SelectControl) {
        var s = control as SelectControl;
        if (s.postAsDisplayName) {
          var idTrim = s.id.substring(0, s.id.length - 2);
          id = idTrim + "_Name";
        }
      }
      form.addControl(id, new FormControl(options));
    }

    this.detailForm = form;

    try {
      if (this.isNew() || this.id == undefined) { // New Entity
        this.setupNewEntity();
        this.removeHiddenElementsAndReorder();
        this.init(this.entity, this.breadCrumbPropertyNames);
      } else {
        this.detailService.byId(this.apiController, this.id)
          .pipe(takeUntil(this.subscription))
          .subscribe({
            next: (data: any) => {
              this.entity = data;
              this.entityChange.emit(this.entity);
              this.entityLoaded.emit(this.entity);
              this.fillFieldValues();
              this.removeHiddenElementsAndReorder();
              this.init(this.entity, this.breadCrumbPropertyNames);
            },
            error: (e: HttpErrorResponse) => {
              var errorMsg: string = e.error;
              if (typeof e.error === "object") {
                errorMsg = e.statusText
              }

              this.feedback.give({ text: errorMsg.substring(0, 100) ?? "Error!", status: "error" });
              this.notificationService.show({ title: "Error", description: errorMsg.substring(0, 100) ?? "Error!", type: "error" });

              //If this is a 404 not found error, redirect to home
              if (e.error.status == 404) {
                var baseUrl = window.location.href.replace(this.router.url, '/');
                window.location.replace(baseUrl);

                this.notificationService.show({ title: "Error", description: "Not Found", type: "error" });
              }
            }
          });
      }
    } catch (err) {
      console.log(err);
    }

    if (this.viewType == undefined) {
      // pull from session

      if (this.viewType == undefined) {
        this.viewType = 'compressed';
      }
    }

    var toolbarSave: ToolbarItem = {
      text: 'Save',/*'general.save',*/
      icon: faSave,
      hasMultiple: false,
      cssClass: "primary-light",
      action: () => { this.onSubmit(); }
    };

    var toolbarRemove: ToolbarItem = {
      text: 'Remove',/*"general.remove",*/
      hasMultiple: false,
      cssClass: "danger",
      icon: faX,
      action: () => { this.openRemoveDialog(); }
    };

    if (!this.removeDefaultToolbarItems) {

      if ((this.showSaveButton) || (this.auth.isAdmin() || window.location.pathname == "/AuthUser/Detail/" + this.auth.getSession()?.userId)) {
        this.toolbarItems.unshift(toolbarSave);
      }
      if (!this.isNew() && this.auth.isAdmin() && (this.showRemoveButton)) { //Only admins should be able to remove stuff
        this.toolbarItems.push(toolbarRemove); 
      }
    }

    this.breadCrumbs = this.breadCrumbService.getBcs();
    this.breadCrumbService.breadCrumbEmitter.subscribe(bcs => {
      this.breadCrumbs = bcs.map(b => b.item);
      //this.cd.detectChanges();
    });
  }

  isFormValid(): boolean {
    var controls = this.flatten();
    var failedValidations: string[] = [];
    for (var key in this.detailForm.value) {
      var value = this.detailForm.value[key];
      var control = controls.filter(f => f.id == key)[0];
      var useDisplayFieldName: boolean = false;
      var displayFieldName: string = control ? control.id : "";
      if (control == undefined) {
        var selectControls = controls.filter(s => s instanceof SelectControl);
        var selects = selectControls.map(c => c as SelectControl);
        var ctrlsPostedAsDisplay = selects.filter(s => s.postAsDisplayName == true);
        var id = key.replace("_Name", "Id");
        control = ctrlsPostedAsDisplay.filter(f => f.id == id)[0];
        useDisplayFieldName = true;
        displayFieldName = key;
      }
      if (control) {
        if (control instanceof SelectControl) {
          var s = control as SelectControl;
          if (s.allowMultiple) {
            if (control.isReadOnly && (s.value == null || s.value.length == 0 || (s.value.length == 1 && s.value[0] == null))) {
              if (useDisplayFieldName) {
                failedValidations.push(displayFieldName);
              } else {
                failedValidations.push(control.id);
              }
            }
          } else {
            if (control.isRequired && (s.singleValue == null || s.singleValue == "" || s.singleValue[s.valueMember] == null)) {
              if (useDisplayFieldName) {
                failedValidations.push(displayFieldName);
              } else {
                failedValidations.push(control.id);
              }
            }
          }
        }
        else {
          if (control.isRequired && (value == null || value == "")) {
            failedValidations.push(control.id);
          }
        }
      }
    }

    if (failedValidations.length > 0) {
      for (var i = 0; i < failedValidations.length; i++) {
        var ctrl = this.detailForm.get(failedValidations[i]);
        if (ctrl) {
          var list = "<ul class='validation-list'>";
          list += "<li>Required field</li>";
          list += "</ul>";
          ctrl.setErrors({ 'error': list });
        }
      }

      this.notificationService.show({ title: "Error", description: "There are errors on the form.", type: "error" });

      return false;
    }

    return true;
  }

  private removeHiddenElementsAndReorder(): void {
    for (var i = 0; i < this.views.length; i++) {
      var view = this.views[i];
      var ctrls = view.controls;
      for (var x = 0; x < ctrls.length; x++) {
        var ctrl = ctrls[x];

        var isGroup = this.isGroup(ctrl);
        if (isGroup) {
          var grp = ctrl as UiGroupControl;
          var rows = grp.rows;

          for (var y = 0; y < rows.length; y++) {
            var row = rows[y];
          var rowCount = grp.rows.length;
          if (row.controls.length == 0) {
            rows.splice(y, 1);
          }
          var cc = row.controls.length;
          var vc = row.controls.filter(c => c.isVisible).length;
          if (cc == vc) {
            continue;
          } else {
            var notV = row.controls.filter(c => !c.isVisible);
            notV.forEach(x => row.controls.splice(row.controls.indexOf(x), 1));

          var nextRow = rows[y + 1];
            if (nextRow) {
              var c = nextRow.controls[0];
              row.controls.push(c);
              nextRow.controls.splice(0, 1);
              }
            }
          }
        }
      }
    }
  }

  private isGroup(control: BaseControl): boolean {
    return control instanceof UiGroupControl;
  }

  isNew() {
    return this.id == "0";
  }

  setupNewEntity() {
    var controls = this.flatten();
    controls.forEach(c => c.loaded = true);

    var isActiveToggles = controls.filter(v => v.id.toLowerCase().includes("active"));
    isActiveToggles.forEach(t => Reflect.set(t, "value", true));
    isActiveToggles.forEach(t => this.detailForm.controls[t.id].setValue(true));
  }

  fillFieldValues() {
    for (var i = 0; i < this.views.length; i++) {
      var view = this.views[i];
      var ctrls = view.controls;
      for (var x = 0; x < ctrls.length; x++) {
        var ctrl = ctrls[x];

        if (ctrl instanceof TabControl) {
          ctrl.loaded = true;
          var tab = ctrl as TabControl;
          tab.tabOptions.data = this.entity[ctrl.id]
          tab.parentEntityId = this.entity[this.keyFieldName];
          continue;
        }

        //this.cd.detectChanges();

        if (ctrl instanceof EntityTypeAttributeTabControl) {
          ctrl.loaded = true;
          var attrTab = ctrl as EntityTypeAttributeTabControl;
          attrTab.entityId = this.entity[this.keyFieldName];
          attrTab.tabOptions.data = this.entity[ctrl.id];
          continue;
        }

       // this.cd.detectChanges();

        var isGroup: boolean = this.isGroup(ctrl);
        if (isGroup) {
          var group = ctrl as UiGroupControl;
          var rows = group.rows;

          var controls = this.flatten();

          var moneyControls = controls.filter(m => m instanceof MoneyControl);
          var urlControls = controls.filter(u => u instanceof UrlControl);
          var listBoxControls = controls.filter(l => l instanceof ListBoxControl);
          var selectControls = controls.filter(s => s instanceof SelectControl);
          var radioControls = controls.filter(r => r instanceof RadioControl);
          var gridControls = controls.filter(g => g instanceof GridControl);
          var customControls: BaseControl[] = [];
          customControls.push.apply(moneyControls);
          customControls.push.apply(urlControls);
          customControls.push.apply(selectControls);
          customControls.push.apply(radioControls);

          // General controls
          var generalControls = controls.filter(c => !customControls.includes(c));
          var patchValue: { [k: string]: any } = {};
          generalControls.forEach(gc => {
            var value = this.entity[gc.id];
            if ((value === undefined || value === null) && gc instanceof ToggleControl) {
              value = false;
            }
            if (gc instanceof DateControl) {
              if (value == null) {
                value = undefined;
              } else {
                value = new Date(value);
              }
            }
            patchValue[gc.id] = value;
          });
          generalControls.forEach(gc => gc.loaded = true);

          //this.cd.detectChanges();

          // Money controls
          moneyControls.forEach(mc => {
            var moneySelect = mc as MoneyControl;
            moneySelect.value = this.entity[mc.id];
            patchValue[mc.id] = this.entity[mc.id];
          });

         // this.cd.detectChanges();

          // Url controls
          urlControls.forEach(uc => {
            var url = uc as UrlControl;
            url.value = this.entity[uc.id];
            patchValue[uc.id] = this.entity[uc.id];
          });

          // Grid controls
          gridControls.forEach(g => {
            var grid = g as GridControl;
            grid.gridOptions.data = this.entity[g.id]
            grid.parentEntityId = this.entity[this.keyFieldName];
          });

          // Radio Controls
          radioControls.forEach(rc => {
            var radioControl = rc as RadioControl;
            var value = this.entity[rc.id];
            var htmlId = (value == undefined || value == null) ? rc.id + "_unspecified" : (value == false || value == "False") ? rc.id + "_false" : rc.id + "_true";
            var htmlElement = document.getElementById(htmlId) as HTMLInputElement;
            if (htmlElement) {
              htmlElement.checked = true;
            }
            patchValue[rc.id] = value;
          });

          // ListBox controls
          listBoxControls.forEach(lb => {
            var list = lb as ListBoxControl;
            list.value = this.entity[lb.id];
            patchValue[lb.id] = this.entity[lb.id];
          });

          // Select controls
          selectControls.forEach(sc => {
            sc.loaded = true;
            var value = this.entity[sc.id];
            var select = sc as SelectControl;
            var idTrim = sc.id.substring(0, sc.id.length - 2);
            var nameValue = this.entity[idTrim + "_Name"];
            if (select.sameValueAndDisplayMembers) {
              nameValue = this.entity[sc.id];
            }
            var selectValue: any = {};
            selectValue[select.valueMember] = value;
            selectValue[select.displayMember] = nameValue;

            if (select.allowMultiple) {
              var multiValuesSelected: boolean = (value != null && typeof value === 'object' && Object.keys(value).length > 0);
              if (multiValuesSelected) {
                var values: any[] = [];
                for (var key in value) {
                  var v: any = {};
                  v[select.valueMember] = parseInt(key);
                  v[select.displayMember] = value[key];
                  values.push(v);
                }
                select.value = values;
              } else {
                select.value = selectValue;
              }
              patchValue[sc.id] = select.value;
            } else {
              select.singleValue = selectValue;
              patchValue[sc.id] = select.singleValue;
            }
          });

          //this.cd.detectChanges();

          this.detailForm.patchValue(patchValue);
          var controlsToDisable = controls.filter(c => c.isReadOnly);
          controlsToDisable.forEach(c => this.detailForm.controls[c.id].disable());

          //this.cd.detectChanges();
        } else {
          var value = this.entity[ctrl.id];
          if (value) {
            var patch: { [k: string]: any } = {};
            patch[ctrl.id] = value;
            this.detailForm.patchValue(patch);
          }
        }
      }
    }
  }

  toGroup(control: BaseControl): UiGroupControl {
    return control as UiGroupControl;
  }

  toTab(element: BaseControl): TabControl {
    return element as TabControl;
  }

  toEntityTypeAttributeTab(element: BaseControl): EntityTypeAttributeTabControl {
    return element as EntityTypeAttributeTabControl;
  }

  toTextArea(control: BaseControl): TextAreaControl {
    return control as TextAreaControl;
  }

  toText(control: BaseControl): TextControl {
    return control as TextControl;
  }

  toEditorText(control: BaseControl): EditorControl {
    return control as EditorControl;
  }

  toListBox(control: BaseControl): ListBoxControl {
    return control as ListBoxControl;
  }

  toPassword(control: BaseControl): PasswordControl {
    return control as PasswordControl;
  }

  toDate(control: BaseControl): DateControl {
    return control as DateControl;
  }

  toToggle(control: BaseControl): ToggleControl {
    return control as ToggleControl;
  }

  onSubmit() {
    var allParameters = this.detailForm.getRawValue();
    var formattedParameters = this.detailForm.value;

    var allKeys = Object.keys(allParameters);
    var formattedKeys = Object.keys(formattedParameters);

    allKeys.forEach(function (x) {
      if (formattedKeys.indexOf(x) == -1) {
        formattedParameters[x] = allParameters[x]
      }
    });

    if (!this.isFormValid()) {
      this.overlayService.close();
      this.notificationService.show({ title: "Error", description: "There are errors on the form.", type: "error" });
      return;
    }

    if (this.preSave == true) {
      this.save.emit(this.formControls);
      // In the preSave we can add extra/custom values to our POST call
      // Reference auth-group.component.ts
      if (this.formControls !== undefined && Object.keys(this.formControls).length > 0) {
        for (var key in this.formControls) {
          var formControl = this.formControls[key];
          this.detailForm.addControl(key, formControl);
        }
      }
    } else if (this.preSave == false) {
      this.save.emit(this.formControls);
      return;
    } else {
      // if null then save as normal
    }

    // values that changed
    var dirtyFieldNames: string[] = Object.keys(this.detailForm.controls).filter(key => { return this.detailForm.get(key)?.dirty; });

    // flatten controls into list
    var allControls: BaseControl[] = this.flatten();

    // get select controls
    var selectControls = allControls.filter(ctrl => ctrl.className === "SelectControl");
    for (var i = 0; i < selectControls.length; i++) {
      var s = selectControls[i] as SelectControl;
      if (s) {
        var selected = s.allowMultiple ? s.value : s.singleValue;
        if (selected == null) {
          this.detailForm.value[s.id] = null;
        } else {
          var value: any;
          if (selected instanceof Array) {
            value = selected.map(x => x[s.valueMember]);
          } else {
            value = selected[s.valueMember];
          }
          this.detailForm.value[s.id] = value;
        }
      }
    }

    //get upload controls
    var uploadControls = allControls.filter(ctrl => ctrl.className === "UploadControl");
    for (var i = 0; i < uploadControls.length; i++) {
      var u = uploadControls[i] as UploadControl;
      if (u) {

        var selectedImage = u.data;
        this.detailForm.value[u.id] = selectedImage;
      }
    }

    //get list controls
    var listBoxControls = allControls.filter(ctrl => ctrl.className === "ListBoxControl");
    for (var i = 0; i < listBoxControls.length; i++) {
      var t = listBoxControls[i] as ListBoxControl;
      if (t) {
        this.detailForm.value[t.id] = t.value;
      }
    }

    // get money controls
    var moneyControls = allControls.filter(ctrl => ctrl.className == 'MoneyControl');
    for (var i = 0; i < moneyControls.length; i++) {
      var m = moneyControls[i] as MoneyControl;
      if (m) {
        this.detailForm.value[m.id] = m.getMoneyValue();
      }
    }

    var id: string;
    if (this.isNew()) {
      id = "0";
    } else {
      var id: string = this.detailForm.value[this.keyFieldName];
      if (!id) {
        id = this.entity[this.keyFieldName];
      }
    }

    this.submitted = true;
    this.overlayService.open();

    //// call save
    this.detailService.save(this.detailForm.value, this.apiController, id, false)
      .pipe(takeUntil(this.subscription))
      .subscribe((response) => {

        //Remove the overlay no matter what
        this.overlayService.close();

        if (response.wasSuccessful) {
          if (this.isNew()) { // if it was a new entity, we need to bind the db entity so we can have the ID
            this.entity = response.entity ?? this.detailForm.value;
            this.init(this.entity);
          }

          //this.feedback.give({ text: response.message ?? "Saved Successfully!", status: "success" });
          this.notificationService.show({ title: "Success", description: response.message ?? "Saved Successfully!", type: "success" });
          this.cd.detectChanges();

        } else {
          // show validation errors
          var errors = response.errors;

          if (errors != null || errors != undefined) {
          for (var key in errors) {
            var prop = key.charAt(0).toLowerCase() + key.slice(1);
            var control = this.detailForm.get(prop);
            if (control) {
              var list = "<ul class='validation-list'>";
              var messages = errors[key].filter(e => !e.valid).map(m => "<li>" + m.message + "</li>").join("");
              list += messages + "</ul>";
              control.setErrors({ 'error': list });
              this.cd.detectChanges();
            }
            }
            this.notificationService.show({ title: "Error", description: "There are errors on the form.", type: "error" });
          } else {
            this.notificationService.show({ title: "Error", description: response.message ?? "Something went wrong!", type: "error" });
          }
        }
        error: (e: HttpErrorResponse) => {
          this.overlayService.close();
          var errorMsg: string = e.error;
          if (typeof e.error === "object") {
            errorMsg = e.statusText
          }
        }
      });
  }

  openRemoveDialog() {
    this.openDialog = true;
  }

  closeRemoveDialog() {
    this.openDialog = false;
  }

  remove() {
    this.dialogLoader = true;
    this.dialogIcon = false;

    this.detailService.remove(this.id, this.apiController).subscribe((response) => {
      this.dialogLoader = false;
      this.dialogIcon = true;
      this.openDialog = false;
      if (response.wasSuccessful) {
        this.feedback.give({ text: "Removed Successfully!", status: "success" });
        this.notificationService.show({ title: "Removed Successfully!", description: "Removed Successfully!", type: "success" });
      } else {
        this.feedback.give({ text: response.message ?? "Error!", status: "error" });
        this.notificationService.show({ title: "Error", description: response.message ?? "Error!", type: "error" });
      }
    });
  }

  private flatten(): BaseControl[] {

    var arrs: BaseControl[][] = this.views.map(v => {

      const view: ViewControl = { ...v };
      var ctrls = view.controls;

      var rows = ctrls.map(g => (g as UiGroupControl).rows);
      var arrRows = rows.reduce((accumulator, value) => accumulator.concat(value), []);

      var controls = arrRows.map(r => r.controls);
      var arrControls = controls.reduce((accumulator, value) => accumulator.concat(value), []);

      var tabControls = ctrls.filter(c => c.className == 'TabControl');
      if (tabControls !== undefined && tabControls.length > 0) {
        tabControls.forEach(t => arrControls.push(t));
      }

      return arrControls;
    });

    return arrs.reduce((accumulator, value) => accumulator.concat(value), []);
  }
}
